/**
 * FavouriteDetailPage — single favourite list detail view
 *
 * Route: /favourites/:listId
 *
 * Features:
 *   - Edit List name (inline text input)
 *   - Share wishlist (modal: Select User dropdown from same customer account)
 *   - Add all items to cart (vibrant-orange button)
 *   - Product cards: Add to cart, remove from list (bookmark icon), View more details
 *   - Toast messages for all actions
 *
 * @module pages/FavouriteDetailPage
 * @since 2026-05-25
 */
import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import Toast from '@components/common/Toast';
import { useFavourites } from '@contexts/FavouritesContext';
import { useCart } from '@contexts/CartContext';
import { useAuth } from '@contexts/AuthContext';
import { DEMO_USERS } from '@services/demoUsers';
import { isCommerceEnabledForProductLine } from '@data/index';

export default function FavouriteDetailPage() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { lists, renameList, removeItem, shareList, markRead } = useFavourites();
  const { dispatch: cartDispatch } = useCart();
  const { account } = useAuth();

  const [toast, setToast] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedShareUser, setSelectedShareUser] = useState('');

  const list = useMemo(() => lists.find(l => l.id === listId), [lists, listId]);

  // Mark as read when viewing
  useState(() => {
    if (list?.isNew) markRead(listId);
  });

  // Users from same customer account (for sharing)
  const shareableUsers = useMemo(() => {
    if (!account?.customerId) return [];
    return DEMO_USERS.filter(u =>
      u.account?.customerId === account.customerId &&
      u.id !== account?.id &&
      u.email !== account?.email &&
      u.roleKey !== 'guest'
    ).map(u => ({ id: u.id, name: u.name, email: u.email }));
  }, [account]);

  if (!list) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 text-center">
        <span className="material-symbols-outlined text-4xl text-n3 block mb-3">bookmark_border</span>
        <p className="text-sm text-n5 font-sandvik-text mb-4">Favourite list not found.</p>
        <Link to="/favourites" className="text-sm text-electric-blue hover:underline font-sandvik-text">
          Back to favourites
        </Link>
      </div>
    );
  }

  function handleStartEdit() {
    setEditName(list.name);
    setIsEditing(true);
  }

  function handleSaveEdit() {
    if (editName.trim()) {
      renameList(listId, editName.trim());
      showToast('List name updated successfully');
    }
    setIsEditing(false);
  }

  function handleAddToCart(item) {
    cartDispatch({
      type: 'ADD_ITEM',
      payload: {
        productId: item.itemCode,
        itemCode: item.itemCode,
        description: item.description,
        unitPrice: 0,
        quantity: 1,
      },
    });
    showToast('Your cart has been updated.');
  }

  function handleAddAllToCart() {
    list.items.forEach(item => {
      cartDispatch({
        type: 'ADD_ITEM',
        payload: {
          productId: item.itemCode,
          itemCode: item.itemCode,
          description: item.description,
          unitPrice: 0,
          quantity: 1,
        },
      });
    });
    showToast('Your cart has been updated.');
  }

  function handleRemoveItem(itemCode) {
    removeItem(listId, itemCode);
    showToast('Product removed from favourites');
  }

  function handleShare() {
    if (!selectedShareUser) return;
    const user = shareableUsers.find(u => u.id === selectedShareUser);
    if (user) {
      shareList(listId, user);
      showToast(`Your favourite ${list.name} shared Successfully`);
      setShowShareModal(false);
      setSelectedShareUser('');
    }
  }

  function showToast(message) {
    setToast({ message, type: 'success' });
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Breadcrumbs items={[
        { label: 'Home', href: '/' },
        { label: 'Favourites', href: '/favourites' },
        { label: list.name },
      ]} />

      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* List name — inline editable */}
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onBlur={handleSaveEdit}
            onKeyDown={e => e.key === 'Enter' && handleSaveEdit()}
            className="font-sandvik-headline text-2xl font-normal text-carbon-black
                       border border-n3 px-2 py-1 flex-1 min-w-0
                       focus:outline-none focus:border-electric-blue"
            autoFocus
          />
        ) : (
          <h1 className="font-sandvik-headline text-2xl font-normal text-carbon-black">
            {list.name}
          </h1>
        )}

        <button
          type="button"
          onClick={handleStartEdit}
          className="text-sm text-carbon-black underline font-sandvik-text hover:text-electric-blue"
        >
          Edit List name
        </button>

        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            className="px-5 py-2.5 border border-n3 text-sm font-sandvik-text text-carbon-black
                       hover:bg-n1 transition-colors"
          >
            Share wishlist
          </button>
          <button
            type="button"
            onClick={handleAddAllToCart}
            disabled={list.items.length === 0}
            className="px-5 py-2.5 bg-electric-blue text-white text-sm font-sandvik-text
                       hover:bg-eb-3 transition-colors disabled:bg-n3 disabled:cursor-not-allowed"
          >
            Add all items to cart
          </button>
        </div>
      </div>

      {/* Empty state */}
      {list.items.length === 0 && (
        <div className="text-center py-12 border border-n2 bg-white">
          <span className="material-symbols-outlined text-4xl text-n3 block mb-3">bookmark_border</span>
          <p className="text-sm text-n5 font-sandvik-text">No products in this list yet.</p>
        </div>
      )}

      {/* Product items */}
      {list.items.length > 0 && (
        <div className="space-y-0">
          {list.items.map(item => (
            <div key={item.itemCode} className="border border-n2 bg-white p-5 flex items-start gap-5">
              {/* Product image */}
              <Link
                to={`/products/detail/${item.itemCode}`}
                className="flex-shrink-0 w-32 h-24 bg-n1 flex items-center justify-center"
              >
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.description} className="w-full h-full object-contain" />
                ) : (
                  <span className="material-symbols-outlined text-4xl text-n3">build</span>
                )}
              </Link>

              {/* Product info */}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/products/detail/${item.itemCode}`}
                  className="text-base font-sandvik-text font-normal text-carbon-black
                             hover:text-electric-blue transition-colors block"
                >
                  {item.description || item.itemCode}
                </Link>
                <p className="text-xs text-n5 font-sandvik-text mt-0.5">
                  Part Number {item.itemCode}
                </p>
                <Link
                  to={`/products/detail/${item.itemCode}`}
                  className="mt-2 inline-flex items-center gap-0.5 text-xs text-carbon-black hover:underline"
                >
                  View more details
                  <span className="material-symbols-outlined text-sm text-vibrant-orange">chevron_right</span>
                </Link>
              </div>

              {/* Actions */}
              <div className="flex-shrink-0 flex flex-col items-end gap-3">
                {isCommerceEnabledForProductLine(item.productLine) && (
                  <button
                    type="button"
                    onClick={() => handleAddToCart(item)}
                    className="px-5 py-2 bg-electric-blue text-white text-sm font-sandvik-text
                               hover:bg-eb-3 transition-colors"
                  >
                    Add to cart
                  </button>
                )}

                {/* Remove from favourites (bookmark icon) */}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(item.itemCode)}
                  title="Remove from favourites"
                  className="w-10 h-10 rounded-full bg-n1 flex items-center justify-center
                             text-vibrant-orange hover:bg-n2 transition-colors"
                >
                  <span className="material-symbols-outlined text-xl">bookmark</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Share modal */}
      {showShareModal && (
        <>
          <div className="fixed inset-0 bg-carbon-black/40 z-50" onClick={() => setShowShareModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between p-6 pb-0">
                <h2 className="font-sandvik-headline text-2xl font-normal text-carbon-black">
                  Share Your Favourite List
                </h2>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-carbon-black hover:text-n5 transition-colors"
                >
                  <span className="material-symbols-outlined text-2xl">close</span>
                </button>
              </div>

              <div className="p-6 bg-n1 mt-4 mx-6 mb-2">
                <p className="text-sm font-sandvik-text font-medium text-carbon-black mb-3">
                  Select User
                </p>
                <select
                  value={selectedShareUser}
                  onChange={e => setSelectedShareUser(e.target.value)}
                  className="w-full px-3 py-2.5 border border-n3 text-sm
                             font-sandvik-text text-carbon-black bg-white
                             focus:outline-none focus:border-electric-blue"
                >
                  <option value="">Select an option</option>
                  {shareableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 p-6 pt-4">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="px-6 py-2.5 border border-n3 text-sm font-sandvik-text
                             text-carbon-black bg-n1 hover:bg-n2 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShare}
                  disabled={!selectedShareUser}
                  className={`px-6 py-2.5 text-sm font-sandvik-text text-white transition-colors
                    ${selectedShareUser
                      ? 'bg-electric-blue hover:bg-eb-3 cursor-pointer'
                      : 'bg-n3 cursor-not-allowed'}`}
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type || 'success'}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
